import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, Code, Server, Users, ShoppingBag, ShieldCheck,
  Cloud, Smartphone, Palette, Layout, CheckCircle2,
  Compass, GraduationCap, MessageSquare, CreditCard, LayoutTemplate,
  FileBadge, Cctv, PackageSearch, Cable, BatteryCharging, ListChecks,
} from "lucide-react";
import { HERO, PARTNERS, SERVICES, ROADMAP_STEPS, SECTORS } from "../data/site";
import SectionHeader from "../components/SectionHeader";
import ProgressRing from "../components/ProgressRing";
import HeroSlider from "../components/HeroSlider";
import TestimonialsCarousel from "../components/TestimonialsCarousel";

const ICONS = {
  Layout, Code, Server, Users, ShoppingBag, ShieldCheck, Cloud, Smartphone, Palette,
  Compass, GraduationCap, MessageSquare, CreditCard, LayoutTemplate, FileBadge, Cctv,
  PackageSearch, Cable, BatteryCharging, ListChecks,
};

export default function Home() {
  return (
    <>
      {/* HERO SLIDER */}
      <HeroSlider />

      {/* SECTORS STRIP */}
      <section className="border-b border-slate-100 bg-white py-10" data-testid="sectors-strip">
        <div className="container-st">
          <p className="text-center text-xs font-bold tracking-[0.25em] uppercase text-slate-500 mb-5">
            Software built for · since 2009
          </p>
          <Marquee gradient gradientColor="#ffffff" gradientWidth={70} speed={45}>
            {SECTORS.concat(SECTORS).map((s, i) => (
              <span
                key={i}
                className="mx-6 inline-flex items-center text-sm sm:text-base font-semibold text-slate-700 px-4 py-1.5 rounded-full bg-slate-50 border border-slate-100"
              >
                {s}
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="border-y border-slate-100 bg-slate-50/60 py-10">
        <div className="container-st">
          <p className="text-center text-xs font-bold tracking-[0.25em] uppercase text-slate-500 mb-6">
            Built with tools from industry innovators.
          </p>
          <Marquee gradient gradientColor="#f8fafc" gradientWidth={80} speed={40}>
            {PARTNERS.concat(PARTNERS).map((p, i) => (
              <span
                key={i}
                className="partner-logo mx-10 text-2xl font-bold tracking-tight text-slate-700"
              >
                {p}
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* FEATURE HIGHLIGHTS */}
      <section className="section-pad">
        <div className="container-st">
          <SectionHeader
            eyebrow="Feature Highlights"
            title="A better employer experience — and faster business growth."
            subtitle="Amcblast Solutions elevates your team's wellness while optimizing the operational levers that compound revenue."
          />
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5 rounded-3xl bg-[#F3E8FF] p-10 flex flex-col items-center justify-center text-center"
            >
              <ProgressRing value={88} />
              <h3 className="mt-6 text-2xl font-semibold text-slate-900">
                88% of clients see growth within 90 days
              </h3>
              <p className="mt-2 text-sm text-slate-600 max-w-xs">
                Measured by revenue, retention, and team productivity post-engagement.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-7 rounded-3xl bg-slate-900 text-white p-10 relative overflow-hidden"
            >
              <img
                src={HERO.feature_graphic}
                alt=""
                className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-30"
              />
              <div className="relative">
                <p className="text-xs font-bold tracking-[0.25em] uppercase text-blue-400">
                  Comprehensive IT Solution
                </p>
                <h3 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight">
                  Growth & efficiency, engineered into every layer.
                </h3>
                <p className="mt-4 text-slate-300 max-w-lg">
                  From customer-facing apps to internal tools and cloud infrastructure — one
                  team, one roadmap, one accountable partner.
                </p>
                <ul className="mt-6 grid grid-cols-2 gap-3 max-w-md">
                  {["Strategy", "Design", "Engineering", "Cloud & DevOps"].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-blue-400" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/solutions"
                  data-testid="features-cta"
                  className="mt-8 inline-flex items-center gap-2 bg-white text-slate-900 font-semibold px-5 py-3 rounded-full hover:bg-slate-100"
                >
                  See Solutions <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad bg-slate-50/60">
        <div className="container-st">
          <SectionHeader
            eyebrow="What We Do"
            title="Our services at a glance."
            subtitle="Amcblast Solutions Limited provides full-cycle services – from planning and design to development, integration, deployment and support – across Web, Mobile & Social applications and supporting infrastructure."
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 9).map((s, idx) => {
              const Icon = ICONS[s.icon];
              return (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  className={`${s.bg} rounded-3xl p-8 group hover:-translate-y-1 transition-transform`}
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="h-12 w-12 rounded-2xl bg-white grid place-items-center shadow-sm">
                    {Icon && <Icon className="w-6 h-6 text-blue-600" />}
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">{s.short}</p>
                  <Link
                    to={`/services/${s.slug}`}
                    className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 group-hover:gap-2 transition-all"
                  >
                    Read more <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/services"
              data-testid="home-view-all-services"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 py-3 rounded-full"
            >
              View all {SERVICES.length} services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="section-pad bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 roadmap-glow pointer-events-none" />
        <img src={HERO.roadmap_bg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="container-st relative">
          <SectionHeader
            eyebrow="Working Roadmap"
            title="A delivery process that respects your time and budget."
            subtitle="Six clear stages — transparent, predictable, and built for outcomes."
            center
            light
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ROADMAP_STEPS.map((r, i) => (
              <motion.div
                key={r.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-7 hover:bg-white/10 transition"
              >
                <div className="text-blue-400 font-mono text-sm tracking-widest">STEP {r.step}</div>
                <h3 className="mt-3 text-xl font-semibold">{r.title}</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{r.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad">
        <div className="container-st">
          <SectionHeader
            eyebrow="Testimonials"
            title="What clients say after working with us."
            subtitle="Real voices from leaders across transport, fintech, hospitality, healthcare, education and energy."
            center
          />
          <TestimonialsCarousel />
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad">
        <div className="container-st">
          <div className="rounded-[2rem] bg-gradient-to-br from-blue-600 to-indigo-700 p-10 sm:p-16 text-white relative overflow-hidden">
            <div className="absolute inset-0 dot-grid opacity-10" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight">
                  Have a project? Speak with our expert.
                </h2>
                <p className="mt-4 text-blue-100 max-w-lg">
                  Tell us about your goals and timelines. We'll respond in under 24 hours with next steps.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 lg:justify-end">
                <Link
                  to="/contact?type=quote"
                  data-testid="bottom-cta-quote"
                  className="btn-shine bg-white text-blue-700 font-semibold px-6 py-3.5 rounded-full"
                >
                  Get a Job Quote
                </Link>
                <Link
                  to="/contact"
                  data-testid="bottom-cta-contact"
                  className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-6 py-3.5 rounded-full"
                >
                  Talk to Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WEATHER + FX BAND (compact, just above footer) */}
    </>
  );
}
